// src/pages/useTableTimerFirebase.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getDatabase, ref, onValue, set, update, serverTimestamp } from "firebase/database";
import { app } from "../firbase";

const db = getDatabase(app);

const MS_IN_HOUR = 3600000;

export default function useTableTimerFirebase(tableKey, ratePerHour) {
  const tableRef = useMemo(() => ref(db, `tableTimers/${tableKey}`), [tableKey]);

  // Remote (DB) state snapshot
  const [remote, setRemote] = useState({
    startTime: null,        // number ms since epoch (first start)
    lastResumeAt: null,     // number ms since epoch (last resume)
    accumulatedMs: 0,       // total elapsed excluding current running segment
    isRunning: false,
    isPaused: false,
    names: [""],
    light: "OFF",
  });

  // Local ticking (no DB writes)
  const [nowMs, setNowMs] = useState(Date.now());
  const tickRef = useRef(null);

  // Subscribe to DB
  useEffect(() => {
    const unsub = onValue(tableRef, (snap) => {
      const data = snap.val();
      if (data) {
        setRemote((prev) => ({
          ...prev,
          ...data,
          // normalize missing fields
          accumulatedMs: Number(data.accumulatedMs || 0),
          isRunning: !!data.isRunning,
          isPaused: !!data.isPaused,
          startTime: data.startTime || null,
          lastResumeAt: data.lastResumeAt || null,
          names: Array.isArray(data.names) ? data.names : [""],
          light: data.light || "OFF",
        }));
      } else {
        // initialize if path empty
        set(tableRef, {
          startTime: null,
          lastResumeAt: null,
          accumulatedMs: 0,
          isRunning: false,
          isPaused: false,
          names: [""],
          light: "OFF",
        });
      }
    });
    return () => unsub();
  }, [tableRef]);

  // Single local ticker for smooth UI (1s)
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const liveElapsedMs = useMemo(() => {
    if (!remote.isRunning) return remote.accumulatedMs || 0;
    if (remote.isPaused || !remote.lastResumeAt) return remote.accumulatedMs || 0;
    // running segment = now - lastResumeAt
    return (remote.accumulatedMs || 0) + Math.max(0, nowMs - remote.lastResumeAt);
  }, [remote.accumulatedMs, remote.isPaused, remote.isRunning, remote.lastResumeAt, nowMs]);

  // Derived values (keep numeric; format in UI)
  const totalHours = liveElapsedMs / MS_IN_HOUR;
  const totalAmountNum = totalHours * ratePerHour;
  const personCount = remote.names.filter(n => n && n.trim() !== "").length || 1;
  const perPersonNum = totalAmountNum / personCount;

  const timeDisplay = useMemo(() => {
    const totalSec = Math.floor(liveElapsedMs / 1000);
    const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
    const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
    const s = String(totalSec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  }, [liveElapsedMs]);

  // Actions (DB writes only on transitions)
  const start = useCallback(async () => {
    const validNames = remote.names.filter(n => n && n.trim() !== "");
    if (validNames.length === 0) {
      alert("Enter at least one name to start!");
      return;
    }
    const now = Date.now();
    await set(tableRef, {
      startTime: now,
      lastResumeAt: now,
      accumulatedMs: 0,
      isRunning: true,
      isPaused: false,
      names: remote.names,
      light: "ON",
    });
  }, [remote.names, tableRef]);

  const pause = useCallback(async () => {
    if (!remote.isRunning || remote.isPaused) {
      // resume
      const now = Date.now();
      await update(tableRef, {
        isPaused: false,
        lastResumeAt: now,
        light: "ON",
      });
    } else {
      // pause
      if (!remote.lastResumeAt) return;
      const now = Date.now();
      const addMs = Math.max(0, now - remote.lastResumeAt);
      await update(tableRef, {
        isPaused: true,
        lastResumeAt: null,
        accumulatedMs: (remote.accumulatedMs || 0) + addMs,
        light: "OFF",
      });
    }
  }, [remote.isRunning, remote.isPaused, remote.lastResumeAt, remote.accumulatedMs, tableRef]);

  const stop = useCallback(async () => {
    // compute final totals locally
    const finalMs = liveElapsedMs;
    const finalMinutes = Math.round(finalMs / 60000);
    const finalAmount = Number((finalMs / MS_IN_HOUR * ratePerHour).toFixed(2));

    // business day key (11:00 to next day 03:00)
    const start = new Date(remote.startTime || Date.now());
    const dayAnchor = new Date(start);
    dayAnchor.setHours(11, 0, 0, 0);
    const endWindow = new Date(dayAnchor);
    endWindow.setDate(endWindow.getDate() + 1);
    endWindow.setHours(3, 0, 0, 0);

    const businessDateKey = (start >= dayAnchor && start < endWindow)
      ? dayAnchor.toISOString().slice(0,10)
      : new Date(dayAnchor.setDate(dayAnchor.getDate() - 1)).toISOString().slice(0,10);

    const sessionKey = `${tableKey}_${Date.now()}`;
    const sessionRef = ref(db, `sessionLogs/${businessDateKey}/${sessionKey}`);

    await set(sessionRef, {
      table: tableKey,
      names: remote.names,
      startTime: remote.startTime,
      endTime: Date.now(),
      totalMinutes: finalMinutes,
      totalAmount: finalAmount,
      ratePerHour,
      splitAmong: remote.names.filter(n => n && n.trim() !== "").length || 1,
      modeOfPayment: null, // fill later from UI
    });

    // reset table
    await set(tableRef, {
      startTime: null,
      lastResumeAt: null,
      accumulatedMs: 0,
      isRunning: false,
      isPaused: false,
      names: [""],
      light: "OFF",
    });
  }, [liveElapsedMs, ratePerHour, remote.names, remote.startTime, tableKey, tableRef]);

  const updateName = useCallback(async (index, value) => {
    const updated = [...remote.names];
    updated[index] = value;
    await update(tableRef, { names: updated });
  }, [remote.names, tableRef]);

  return {
    startTime: remote.startTime,
    isRunning: remote.isRunning,
    isPaused: remote.isPaused,
    names: remote.names,

    // derived & formatted for UI
    timeDisplay,
    totalAmount: totalAmountNum,   // number
    perPerson: perPersonNum,       // number
    totalMinutes: Math.round(liveElapsedMs / 60000),

    // actions
    start,
    pause,
    stop,
    updateName,
  };
}
